import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AiService } from './ai.service';
import { DashboardService } from './dashboard.service';

export interface RoadmapItemModel {
  id: string;
  pathwayId: string;
  pathwayTitle: string;
  moduleId: string;
  moduleTitle: string;
  estimatedHours: number;
  deadline: string;
  completed: boolean;
  recommendedHours: number;
}

export interface LearningRoadmapModel {
  generatedAt: string;
  items: RoadmapItemModel[];
  warnings: string[];
}

@Injectable({
  providedIn: 'root'
})
export class LearningRoadmapService {
  constructor(
    private dashboardService: DashboardService,
    private aiService: AiService
  ) {}

  generateRoadmap(pathwayId: string, provider: 'gemini' | 'claude' = 'claude'): Observable<LearningRoadmapModel> {
    return this.dashboardService.getPathwayDetail(pathwayId).pipe(
      switchMap((pathwayRes: any): Observable<LearningRoadmapModel> => {
        const pathwayData = (pathwayRes && pathwayRes.data) || {};
        const modules = pathwayData.videos || [];
        const normalizedPathwayData = {
          _id: pathwayData._id || pathwayId,
          title: pathwayData.title || 'Current Tangram',
          videos: modules
        };

        if (!modules.length) {
          return of<LearningRoadmapModel>({
            generatedAt: new Date().toISOString(),
            items: [],
            warnings: []
          });
        }

        return this.dashboardService.getMyTangramList().pipe(
          switchMap((myTangramRes: any): Observable<LearningRoadmapModel> => {
            const allCourses = (myTangramRes && myTangramRes.data) || [];
            const pathwayIds = allCourses.map((course: any) => course._id).filter((id: string) => !!id);

            const completions$ = pathwayIds.length
              ? this.dashboardService.getBatchMediaCompletions(pathwayIds).pipe(catchError(() => of({ data: [] })))
              : of({ data: [] });

            return completions$.pipe(
              switchMap((batchCompletionsRes: any): Observable<LearningRoadmapModel> => {
                const completionMap = this.buildCompletionMap((batchCompletionsRes && batchCompletionsRes.data) || []);
                const heuristicRoadmap = this.buildHeuristicRoadmap(normalizedPathwayData, modules, allCourses, completionMap);
                const aiPayload = {
                  pathwayId,
                  pathwayTitle: normalizedPathwayData.title,
                  modules: heuristicRoadmap.items.map((item) => ({
                    moduleId: item.moduleId,
                    moduleTitle: item.moduleTitle,
                    estimatedHours: item.estimatedHours,
                    recommendedHours: item.recommendedHours,
                    deadline: item.deadline,
                    completed: item.completed
                  })),
                  concurrentCourses: allCourses.map((course: any) => ({ pathwayId: course._id, title: course.title }))
                };

                return this.aiService.getLearningRoadmap(aiPayload, provider).pipe(
                  map((aiRes: any) => this.mergeAiRoadmap(heuristicRoadmap, aiRes)),
                  catchError(() => of<LearningRoadmapModel>({
                    ...heuristicRoadmap,
                    warnings: [...heuristicRoadmap.warnings, 'AI roadmap generation failed. Showing adaptive fallback roadmap.']
                  }))
                );
              })
            );
          }),
          catchError((): Observable<LearningRoadmapModel> => {
            return of<LearningRoadmapModel>(this.buildHeuristicRoadmap(
              normalizedPathwayData,
              modules,
              [normalizedPathwayData],
              {}
            ));
          })
        );
      }),
      catchError(() => of<LearningRoadmapModel>({
        generatedAt: new Date().toISOString(),
        items: [],
        warnings: ['Unable to load Tangram data for roadmap generation.']
      }))
    );
  }

  recalculateAdaptiveRoadmap(roadmap: LearningRoadmapModel): LearningRoadmapModel {
    const now = new Date();
    const updated = roadmap.items.map((item) => {
      if (item.completed) {
        return { ...item, recommendedHours: 0 };
      }
      const deadline = new Date(item.deadline);
      const remainingDays = Math.max(1, Math.ceil((deadline.getTime() - now.getTime()) / 86400000));
      const adaptiveHours = Number((item.estimatedHours / Math.min(7, remainingDays)).toFixed(1));
      return {
        ...item,
        recommendedHours: adaptiveHours
      };
    });
    return {
      ...roadmap,
      items: updated
    };
  }

  private buildCompletionMap(batchData: any[]): { [pathwayId: string]: string[] } {
    return (batchData || []).reduce((acc: any, row: any) => {
      acc[row.pathwayId] = row.completedVideoIds || [];
      return acc;
    }, {});
  }

  private buildHeuristicRoadmap(pathwayData: any, modules: any[], allCourses: any[], completionMap: { [pathwayId: string]: string[] }): LearningRoadmapModel {
    const pathwayDeadline = this.getDefaultDeadline(14);
    const concurrentCoursesCount = Math.max(1, allCourses.length);
    const workloadBalanceFactor = Number((1 / concurrentCoursesCount).toFixed(2));
    const completedIds = completionMap[pathwayData._id] || [];

    const items: RoadmapItemModel[] = modules.map((module: any, index: number) => {
      const estimatedHours = this.estimateModuleHours(module, index);
      const completed = completedIds.includes(module._id);
      const deadline = this.getDefaultDeadline((index + 1) * 3);
      const recommendedHours = completed ? 0 : Number((estimatedHours * (1 + (1 - workloadBalanceFactor))).toFixed(1));

      return {
        id: `${pathwayData._id}-${module._id}`,
        pathwayId: pathwayData._id,
        pathwayTitle: pathwayData.title,
        moduleId: module._id,
        moduleTitle: module.title || `Module ${index + 1}`,
        estimatedHours,
        deadline: deadline > pathwayDeadline ? pathwayDeadline : deadline,
        completed,
        recommendedHours
      };
    });

    return {
      generatedAt: new Date().toISOString(),
      items,
      warnings: []
    };
  }

  private mergeAiRoadmap(fallbackRoadmap: LearningRoadmapModel, aiResponse: any): LearningRoadmapModel {
    const aiItems = (aiResponse && aiResponse.data && aiResponse.data.items) || [];
    if (!aiItems.length) {
      return fallbackRoadmap;
    }

    const merged = fallbackRoadmap.items.map((item) => {
      const aiItem = aiItems.find((x: any) => x.moduleId === item.moduleId);
      if (!aiItem) {
        return item;
      }
      return {
        ...item,
        estimatedHours: Number(aiItem.estimatedHours || item.estimatedHours),
        recommendedHours: Number(aiItem.recommendedHours || item.recommendedHours),
        deadline: aiItem.deadline || item.deadline
      };
    });

    return {
      generatedAt: new Date().toISOString(),
      items: merged,
      warnings: (aiResponse && aiResponse.data && aiResponse.data.warnings) || []
    };
  }

  private estimateModuleHours(module: any, moduleIndex: number): number {
    const baseMinutes = Number((module && module.durationMIN) || 20);
    const baseHours = baseMinutes > 0 ? baseMinutes / 60 : 0.5;
    const complexityBuffer = (moduleIndex % 3) * 0.25;
    return Number((Math.max(0.5, baseHours + complexityBuffer)).toFixed(1));
  }

  private getDefaultDeadline(daysFromNow: number): string {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().slice(0, 10);
  }
}
